/**
 * Structured client-side logger for tracking users across the LP: every
 * page they visit, every action they take (send OTP, verify OTP, header
 * enrichment, anti-fraud, campaign lookup), and every API call — in plain,
 * human-readable messages, not just internal function names.
 *
 * Every entry is printed to the browser console AND buffered for shipping
 * to the server (POST {REACT_APP_LOG_ENDPOINT || "/client-logs"}), where
 * server/index.js persists it to a file and stdout so it's visible via
 * `journalctl -u <service>` / the app log file even though this code runs
 * entirely in the visitor's browser. Deliberately not under /api — see the
 * comment at the top of server/index.js for why.
 *
 * Every entry automatically carries:
 *   - userId      stable per-visitor id (Microsoft Clarity's id when
 *                 available, else a local fallback — see clarity.js)
 *   - sessionId   per browser tab/session id
 *   - requestId   fresh id per logical user action/API call (from
 *                 startOperation), so its start/success/fail lines can be
 *                 correlated as "one request"
 *   - page        the current route (pathname + query)
 *   - pageName    a human-readable page label (see setPageName below)
 *
 * Timing: every action wrapped in startOperation gets a start + a
 * success/fail line with `durationMs` (e.g. how long the send-OTP API call
 * took). setPageName additionally emits a `time_on_page`/`durationMs` line
 * every time the visitor moves off a page, so "how long were they on the
 * PIN page" is answerable the same way as "how long did the API call take."
 *
 * PII: OTP/password/token values are never logged. MSISDN is logged in
 * full — supportability (matching a visitor to a support conversation)
 * outweighs masking here.
 */
import { getUserIdentifier } from "./clarity";

const LOG_ENDPOINT =
  (typeof process !== "undefined" && process.env.REACT_APP_LOG_ENDPOINT) || "/client-logs";
const IS_PROD = typeof process !== "undefined" && process.env.NODE_ENV === "production";

const FLUSH_INTERVAL_MS = 4000;
const MAX_BATCH_SIZE = 20;
const MAX_BUFFER_SIZE = 300;
const MAX_JOURNEY_STEPS = 200;

const LEVELS = { DEBUG: 10, INFO: 20, WARN: 30, ERROR: 40 };
// In production, skip DEBUG in the console to avoid noise — it's still shipped.
const MIN_CONSOLE_LEVEL = IS_PROD ? LEVELS.INFO : LEVELS.DEBUG;

const SESSION_STORAGE_KEY = "mf_session_id";

const FULLY_REDACT_PATTERN = /otp|pin|password|token/i;

function generateId() {
  return (
    window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

function getSessionId() {
  try {
    let id = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = generateId();
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch {
    return "no-session-storage";
  }
}

const sessionId = typeof window !== "undefined" ? getSessionId() : "server";

function currentPage() {
  try {
    return window.location.pathname + window.location.search;
  } catch {
    return "unknown";
  }
}

function now() {
  return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
}

/** Drops any key that looks like OTP/PIN/password/token before logging. */
export function redactParams(params) {
  if (!params || typeof params !== "object") return params;

  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => {
      if (FULLY_REDACT_PATTERN.test(key)) return [key, "[REDACTED]"];
      return [key, value];
    })
  );
}

function serializeError(error) {
  if (!error) return null;
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }
  return { message: String(error) };
}

let buffer = [];
let flushTimer = null;

function doFlush(useBeacon) {
  if (buffer.length === 0) return;

  const batch = buffer.splice(0, MAX_BATCH_SIZE);
  const payload = JSON.stringify({ entries: batch });

  try {
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      const accepted = navigator.sendBeacon(LOG_ENDPOINT, blob);
      if (!accepted) throw new Error("sendBeacon rejected payload");
    } else {
      fetch(LOG_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch(() => {
        // Best-effort only — log shipping must never break the app.
      });
    }
  } catch {
    // Best-effort only.
  }

  if (buffer.length > 0) scheduleFlush(false);
}

function scheduleFlush(immediate) {
  if (immediate) {
    doFlush(false);
    return;
  }
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    doFlush(false);
  }, FLUSH_INTERVAL_MS);
}

// --- Ambient "what page is the visitor on right now" ------------------
// Set once per route by App.js's RouteViewLogger (which resolves a friendly
// name like "LP 8888 — PIN Entry" from the route). Every log line records
// this automatically so hooks/API calls deep in a shared file (usePinFlow,
// BaseApiClient, ...) don't each need to be told what page called them.
let currentPageName = "unknown";
let currentPagePath = null;
let currentPageEnteredAt = null;

/**
 * Logs how long the visitor was on the page being left — fired right before
 * `currentPageName` flips to the next page (see setPageName) and again from
 * markLpExit for whichever page they were on when they finally left, so the
 * very last page of a visit still gets a dwell-time line even though it
 * never "changes" to a next page.
 */
function closeCurrentPageDwellTime() {
  if (currentPageEnteredAt === null) return;

  const durationMs = Math.round(now() - currentPageEnteredAt);

  record(LEVELS.INFO, "INFO", "page_view", "time_on_page", `Visitor spent ${durationMs}ms on the "${currentPageName}" page`, {
    path: currentPagePath,
    durationMs,
  });

  currentPageEnteredAt = null;
}

// --- The visitor's journey through the LP, start to finish -------------
const journeySteps = [];

function pushJourneyStep(levelName, operation, fn, message) {
  journeySteps.push({ ts: new Date().toISOString(), pageName: currentPageName, level: levelName, operation, fn, message });
  if (journeySteps.length > MAX_JOURNEY_STEPS) journeySteps.shift();
}

function record(level, levelName, operation, fn, message, data) {
  const { requestId, ...restData } = data || {};

  const entry = {
    ts: new Date().toISOString(),
    level: levelName,
    userId: getUserIdentifier(),
    sessionId,
    requestId: requestId || null,
    page: currentPage(),
    pageName: currentPageName,
    operation: operation || null,
    fn: fn || null,
    message,
    ...(Object.keys(restData).length ? { data: restData } : {}),
  };

  if (level >= MIN_CONSOLE_LEVEL) {
    const consoleFn = level >= LEVELS.ERROR ? console.error : level >= LEVELS.WARN ? console.warn : console.log;
    const tag = `[MF][${levelName}][${currentPageName}]${operation ? `[${operation}]` : ""}`;
    if (Object.keys(restData).length) consoleFn(`${tag} ${message}`, restData);
    else consoleFn(`${tag} ${message}`);
  }

  pushJourneyStep(levelName, operation, fn, message);

  if (buffer.length < MAX_BUFFER_SIZE) buffer.push(entry);
  // Errors/warnings ship immediately; info/debug batch on the interval.
  scheduleFlush(level >= LEVELS.WARN);

  return entry;
}

export const logger = {
  debug: (operation, fn, message, data) => record(LEVELS.DEBUG, "DEBUG", operation, fn, message, data),
  info: (operation, fn, message, data) => record(LEVELS.INFO, "INFO", operation, fn, message, data),
  warn: (operation, fn, message, data) => record(LEVELS.WARN, "WARN", operation, fn, message, data),
  error: (operation, fn, message, data) => record(LEVELS.ERROR, "ERROR", operation, fn, message, data),

  /**
   * Wraps one user action/API call (send OTP, verify OTP, header
   * enrichment...) with a start log and a paired success/fail log carrying
   * its duration — all three share one `requestId` so they can be
   * correlated as a single request even though they're separate log lines.
   * @param {string} operation
   * @param {string} fn
   * @param {object} [data]
   * @returns {{success: (message?: string, data?: object) => void, fail: (error: unknown, message?: string, data?: object) => void}}
   */
  startOperation(operation, fn, data, startMessage = "Started") {
    const requestId = generateId();
    const startedAt = now();
    record(LEVELS.INFO, "INFO", operation, fn, startMessage, { ...data, requestId });

    return {
      requestId,
      success: (message = "Completed successfully", extra) =>
        record(LEVELS.INFO, "INFO", operation, fn, message, {
          ...extra,
          requestId,
          durationMs: Math.round(now() - startedAt),
        }),
      fail: (error, message = "Failed", extra) =>
        record(LEVELS.ERROR, "ERROR", operation, fn, message, {
          ...extra,
          requestId,
          error: serializeError(error),
          durationMs: Math.round(now() - startedAt),
        }),
    };
  },

  /**
   * Sets the human-readable page name every subsequent log line will carry,
   * until the next page change — and closes out a `time_on_page` duration
   * for whichever page the visitor is leaving.
   * @param {string} name
   * @param {string} [path] The route path of the page becoming active (used for the *next* close-out, not this one).
   */
  setPageName(name, path) {
    closeCurrentPageDwellTime();
    currentPageName = name || "unknown";
    currentPagePath = path || null;
    currentPageEnteredAt = now();
  },

  getPageName: () => currentPageName,

  /** Returns a shallow copy of every step recorded so far this session — the full "what did this visitor do" trail. */
  getJourney: () => journeySteps.slice(),

  /** Forces an immediate flush — used sparingly (e.g. right before a redirect navigates away). */
  flush: () => doFlush(false),
};

let lpEnteredAt = null;
let lpExitLogged = false;

/**
 * Marks the moment the visitor's browser started running this app — as
 * close as we get, client-side, to "the second the user entered the LP".
 * Called once, at the top of src/index.js. Logs the full landing params
 * (redacted only for genuinely sensitive fields) so every visit's entry
 * point is fully captured — campaign id, tracker id, gclid/gbraid/wbraid,
 * utm_*, p1-p15, carrier callback params, etc.
 */
export function markLpEnter() {
  if (lpEnteredAt) return;
  lpEnteredAt = Date.now();

  let rawParams = {};
  try {
    rawParams = Object.fromEntries(new URLSearchParams(window.location.search));
  } catch {
    // ignore
  }

  record(LEVELS.INFO, "INFO", "lp_session", "enter", "Visitor landed on the LP", {
    path: window.location.pathname,
    params: redactParams(rawParams),
    referrer: (typeof document !== "undefined" && document.referrer) || null,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
  });
}

/**
 * Marks the moment the visitor left — fires once, on whichever of
 * `pagehide`/tab-hidden happens first (mirrors how most analytics tools
 * treat "session end", since `pagehide` alone is unreliable on some mobile
 * browsers).
 *
 * This is the one log line meant to answer "what did this visitor actually
 * do, start to finish" on its own, without needing to reassemble it from
 * every other line in the file — everything else is the raw materials;
 * this is the report. Alongside the structured `journey` array (for
 * tooling), it includes a plain-English `journeyNarrative`: every step's
 * own human-readable message, in order, prefixed with the page it happened
 * on — since every message logged throughout this file is already written
 * in plain English, no separate summarization logic is needed to build it.
 */
function markLpExit() {
  if (lpExitLogged || !lpEnteredAt) return;
  lpExitLogged = true;

  // The last page the visitor was on never "changes" to a next page, so it
  // would otherwise never get a time_on_page line — close it out here.
  closeCurrentPageDwellTime();

  const durationMs = Date.now() - lpEnteredAt;
  const journey = journeySteps.slice();

  const journeyNarrative = journey.map(
    (step, index) => `${index + 1}. [${step.pageName}] ${step.message}`
  );

  // "unknown" shows up on the bootstrap step logged before routing resolves
  // a page name (markLpEnter runs before <App> even mounts) — not a real
  // page the visitor saw, so it's excluded here even though it's kept as-is
  // in the raw journey/narrative for accuracy.
  const pagesVisited = [...new Set(journey.map((step) => step.pageName))].filter((name) => name !== "unknown");

  record(
    LEVELS.INFO,
    "INFO",
    "lp_session",
    "exit",
    `Visitor's full journey on the LP, start to finish: ${journey.length} steps across ${pagesVisited.length} page(s) over ${durationMs}ms`,
    {
      durationMs,
      lastPage: currentPage(),
      lastPageName: currentPageName,
      pagesVisited,
      journeyNarrative,
      journey,
    }
  );

  doFlush(true);
}

if (typeof window !== "undefined") {
  // Flush on tab hide/close — sendBeacon works even as the page unloads.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      markLpExit();
      doFlush(true);
    }
  });
  window.addEventListener("pagehide", () => {
    markLpExit();
    doFlush(true);
  });
}

export function initGlobalErrorLogging() {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    logger.error("uncaught_error", "window.onerror", `Something broke unexpectedly: ${event.message}`, {
      source: event.filename,
      line: event.lineno,
      column: event.colno,
      error: serializeError(event.error),
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    logger.error("uncaught_error", "window.onunhandledrejection", "A background request failed silently (unhandled promise rejection)", {
      error: serializeError(event.reason),
    });
  });
}
